/**
 * Config drift API client.
 * Implements DRIFT_DASHBOARD_IMPLEMENTATION_PLAN.md §1.3.
 */

import { api } from './api';

export type ConfigType = 'zones' | 'policies' | 'lines' | 'drb' | 'fcp';
export type ConfigStatus = 'converged' | 'drifted' | 'pending' | 'unknown';
export type AppliedSource = 'edge_health' | 'db_ack' | 'none';

export interface BundleRef {
  bundle_hash: string;
  bundle_version: number;
  published_at?: string;
  deployment_id?: string;
}

export interface AppliedRef {
  bundle_hash: string;
  bundle_version: number;
  applied_at: string;
  source: AppliedSource;
}

/** Phase D: What consumers have reloaded (applied != activated when receivers wrote but consumers haven't reloaded). */
export interface ActivatedRef {
  bundle_hash: string;
  bundle_version: number;
  activated_at: string | null;
  source: string;
}

export interface TypeDrift {
  status: ConfigStatus;
  reason_code: string | null;
  desired: BundleRef | null;
  applied: AppliedRef | null;
  hash_match: boolean | null;
  stale_minutes: number | null;
  activated: ActivatedRef | null;
}

export interface EdgeMerge {
  attempted: boolean;
  succeeded: boolean;
  timeout_ms: number;
  error: string | null;
}

export interface ConfigDriftResponse {
  computed_at: string;
  stale_threshold_minutes: number;
  device_id: string;
  overall_status: ConfigStatus;
  edge_merge?: EdgeMerge;
  types: Record<string, TypeDrift>;
}

export async function getConfigDrift(
  deviceId: string,
  options?: { types?: ConfigType[]; timeout_ms?: number }
): Promise<ConfigDriftResponse> {
  const params: Record<string, string> = {};
  if (options?.types?.length) {
    params.types = options.types.join(',');
  }
  if (options?.timeout_ms != null) {
    params.timeout_ms = String(options.timeout_ms);
  }
  const res = await api.get<ConfigDriftResponse>(
    `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/config_drift`,
    { params }
  );
  return res.data;
}
